import { STORE_NAME } from './reducer';
/**
 * Returns whether the user has NKF-AEC echo cancellation enabled.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecEnabled(state) {
    return state[STORE_NAME]?.enabled ?? true;
}
/**
 * Returns whether the NKF-AEC debug waveform overlay ("スピーカー") is shown.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfDebugOverlayOpen(state) {
    return state[STORE_NAME]?.debugOverlay ?? false;
}
/**
 * Returns whether the NKF-AEC debug LOG panel ("ログ") is shown.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfLogOpen(state) {
    return state[STORE_NAME]?.logPanel ?? false;
}
/**
 * The NKF-AEC toolbar button is only offered when the AEC engine itself is
 * enabled via config (`config.nkfAec.enabled`).
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecButtonEnabled(state) {
    return Boolean(state['features/base/config'].nkfAec?.enabled);
}
/**
 * Returns whether echo cancellation has actually reached a working state.
 *
 * False from join until the echo delay is found (or the safety timeout fires).
 * Observation only — no audio path reads this.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecReady(state) {
    return state[STORE_NAME]?.ready ?? false;
}
/**
 * Returns what decided the current readiness ('calibration' | 'tracker' |
 * 'erle' | 'timeout' | '').
 *
 * @param {IReduxState} state - The redux state.
 * @returns {string}
 */
export function getNkfAecReadyReason(state) {
    return state[STORE_NAME]?.readyReason ?? '';
}
/**
 * Returns whether the AEC stopped producing audio altogether (as opposed to
 * merely still converging). Distinguished so the UI can show a fault rather
 * than an indefinite "preparing".
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecStalled(state) {
    return getNkfAecReadyReason(state) === 'stalled';
}
/**
 * Returns whether the echo delay is known but the mic is still held silent
 * while the adaptive filter converges.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecSettling(state) {
    return Boolean(state[STORE_NAME]?.settling) && !isNkfAecReady(state);
}
/**
 * Returns whether the microphone is currently NOT reaching the other side —
 * either nothing is known about the echo yet, or the delay is known but the
 * grace period has not elapsed. Both are silence to the far side.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfAecHoldingMic(state) {
    return isNkfAecEnabled(state) && !isNkfAecReady(state);
}
/**
 * Returns whether the deliberate main-thread jank is currently running.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfJankRunning(state) {
    return state[STORE_NAME]?.jank ?? false;
}
/**
 * Returns whether the main-thread jank button should be offered at all.
 *
 * Debug tool: off unless config.nkfAec.jankButton is explicitly true, so it
 * never reaches a normal deployment.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfJankButtonEnabled(state) {
    return Boolean(state['features/base/config'].nkfAec?.jankButton);
}
/**
 * Returns whether the media stats panel is currently shown.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfMediaStatsOpen(state) {
    return state[STORE_NAME]?.mediaStats ?? false;
}
/**
 * Returns the share of chunks currently bypassed on purpose, 0..100 (debug).
 *
 * @param {IReduxState} state - The redux state.
 * @returns {number}
 */
export function getNkfForceBypass(state) {
    return state[STORE_NAME]?.forceBypass ?? 0;
}
/**
 * 音量の自動調整(メイクアップゲイン)が有効かどうかを返す。
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfMakeupGainOn(state) {
    return state[STORE_NAME]?.makeupGain ?? false; // ★2026-10-09 既定 OFF
}
/**
 * 送信メーターを表示しているかどうかを返す。
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isNkfTxMeterOpen(state) {
    return state[STORE_NAME]?.txMeter ?? false;
}
